import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Form,
  Modal,
  Spinner,
} from 'react-bootstrap';
import AdminSidebar from '../../components/AdminSidebar';
import {
  AdminNotificationsProvider,
  useAdminNotifications,
} from '../../components/AdminNotifications';
import { buildApiUrl } from '../../config/apiConfig';
import useAdminGuard from '../../hooks/useAdminGuard';
import { hasAnyPermission, PERMISSION_IDS } from '../../utils/permissions';
import './AdminRolesPage.css';
import { logError } from '../../utils/logger';

// Categorías de permisos según init-roles-permissions.js
const PERMISSION_CATEGORIES = {
  NAVBAR: {
    label: 'Navegación',
    icon: 'fas fa-bars',
    permissions: ['VerBotonPanel'],
  },
  DASHBOARD: {
    label: 'Dashboard',
    icon: 'fas fa-chart-line',
    permissions: ['VerPanelDashboard'],
  },
  CURSOS: {
    label: 'Cursos',
    icon: 'fas fa-graduation-cap',
    permissions: [
      'VerPanelCursos',
      'VerCursos',
      'VerContenidoCursos',
      'EditarContenidoCursos',
      'EditarExamenesCursos',
      'ActivarCursos',
      'ArchivarCurso',
    ],
  },
  USUARIOS: {
    label: 'Usuarios',
    icon: 'fas fa-users',
    permissions: ['VerUsuarios', 'EditarUsuario', 'EliminarUsuario'],
  },
  ESTADISTICAS: {
    label: 'Estadísticas',
    icon: 'fas fa-chart-bar',
    permissions: ['VerEstadisticas', 'VerTransacciones'],
  },
  NEWSLETTER: {
    label: 'Newsletter',
    icon: 'fas fa-newspaper',
    permissions: [
      'GestionarNewsletter',
      'CrearNewsletter',
      'EditarNewsletter',
      'ArchivarNewsletter',
      'PublicarNewsletter',
      'VerNewsletterAdmin',
      'VerTodosNewsletterAdmin',
      'VerNewslettersArchivados',
      'EstadisticasNewsletter',
    ],
  },
  EVENTOS: {
    label: 'Eventos',
    icon: 'fas fa-calendar-alt',
    permissions: [
      'GestionarEventos',
      'VerEventos',
      'VerEventosArchivados',
      'CrearEventos',
      'EditarEventos',
      'ActivarEventos',
      'ArchivarEventos',
      'EliminarEventos',
    ],
  },
  TIENDA: {
    label: 'Tienda/E-Commerce',
    icon: 'fas fa-shopping-cart',
    permissions: ['GestionarTienda'],
  },
  BUNDLES: {
    label: 'Bundles',
    icon: 'fas fa-box',
    permissions: [
      'VerBundles',
      'CrearBundles',
      'EditarBundles',
      'EliminarBundles',
      'GestionarBundles',
    ],
  },
  PANEL_ADMIN: {
    label: 'Panel Administrativo',
    icon: 'fas fa-cog',
    permissions: [
      'PanelAdmin',
      'PanelUsers',
      'PanelProductos',
      'PanelCursos',
      'PanelEventos',
      'PanelAsesorias',
      'PanelMailing',
      'PanelVentas',
      'PanelItems',
      'PanelNewsletter',
      'PanelLogistica',
    ],
  },
  ITEMS: {
    label: 'Items',
    icon: 'fas fa-cube',
    permissions: [
      'CrearItems',
      'VerTodosItems',
      'VerItems',
      'EditarItems',
      'ActivarItems',
      'ArchivarItems',
      'VerItemsArchivados',
    ],
  },
  CATEGORIAS: {
    label: 'Categorías',
    icon: 'fas fa-tags',
    permissions: ['CrearCategoria', 'EditarCategoria', 'ArchivarCategoria'],
  },
  ROLES: {
    label: 'Roles',
    icon: 'fas fa-user-shield',
    permissions: ['VerRoles', 'CrearRoles', 'EditarRoles', 'EliminarRoles'],
  },
  MAILING: {
    label: 'Mailing',
    icon: 'fas fa-envelope',
    permissions: ['EnviarMails', 'VerMails', 'VerDatosUsuariosMails'],
  },
  PRUEBAS: {
    label: 'Pruebas / QA',
    icon: 'fas fa-flask',
    permissions: ['Tester'],
  },
};

const AdminRolesPageInner = () => {
  const { user, token } = useAdminGuard();
  const navigate = useNavigate();
  const { notify } = useAdminNotifications();

  const canAccessRoles = useMemo(
    () =>
      hasAnyPermission(user, [
        PERMISSION_IDS.VER_ROLES,
        PERMISSION_IDS.CREAR_ROLES,
        PERMISSION_IDS.EDITAR_ROLES,
        PERMISSION_IDS.ELIMINAR_ROLES,
      ]),
    [user],
  );

  useEffect(() => {
    if (user && !canAccessRoles) {
      notify({
        title: 'Acceso denegado',
        message: 'No tienes permisos para gestionar roles.',
        variant: 'error',
      });
      navigate('/403', { replace: true });
    }
  }, [user, canAccessRoles, navigate, notify]);

  // Estados de roles
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [permissions, setPermissions] = useState([]);

  // Estados de modales
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showViewRoleModal, setShowViewRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [editingRole, setEditingRole] = useState(null);

  // Estados de formularios
  const [roleForm, setRoleForm] = useState({
    nombre: '',
    descripcion: '',
    permisos: [],
  });

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    }),
    [token],
  );

  // Helper para normalizar permisos a array de nombres (strings)
  const normalizePermissions = useCallback((permisos) => {
    if (!permisos) return [];
    
    // Si es un array
    if (Array.isArray(permisos)) {
      // Si los elementos son objetos, extraer el nombre
      if (permisos.length > 0 && typeof permisos[0] === 'object' && permisos[0] !== null) {
        return permisos
          .map((perm) => {
            // Si es un objeto con nombre, usar el nombre
            if (perm.nombre) return perm.nombre;
            // Si es un objeto con id pero sin nombre, usar el id como string
            if (perm.id) return String(perm.id);
            // Si es un string dentro del objeto
            if (typeof perm === 'string') return perm;
            return null;
          })
          .filter(Boolean); // Filtrar valores null/undefined
      }
      // Si ya son strings, devolverlos tal cual
      return permisos.filter((p) => typeof p === 'string');
    }
    
    // Si es un string, intentar parsearlo
    if (typeof permisos === 'string') {
      try {
        const parsed = JSON.parse(permisos);
        if (Array.isArray(parsed)) {
          // Si los elementos son objetos, extraer el nombre
          if (parsed.length > 0 && typeof parsed[0] === 'object' && parsed[0] !== null) {
            return parsed
              .map((perm) => {
                if (perm.nombre) return perm.nombre;
                if (perm.id) return String(perm.id);
                return null;
              })
              .filter(Boolean);
          }
          return parsed.filter((p) => typeof p === 'string');
        }
        return [];
      } catch {
        // Si no se puede parsear, tratarlo como un solo permiso
        return [permisos];
      }
    }
    
    return [];
  }, []);

  // Cargar roles
  const loadRoles = useCallback(async () => {
    if (!token) return;
    setLoadingRoles(true);
    try {
      const response = await fetch(buildApiUrl('/api/roles'), {
        headers: authHeaders,
      });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = await response.json();
      // Normalizar permisos de cada rol
      const normalizedRoles = (data.roles || []).map((role) => ({
        ...role,
        permisos: normalizePermissions(role.permisos),
      }));
      setRoles(normalizedRoles);
    } catch (error) {
      logError('[AdminRoles] Error loading roles:', error);
      notify({
        title: 'Error al cargar roles',
        message: 'No se pudieron cargar los roles.',
        variant: 'error',
      });
    } finally {
      setLoadingRoles(false);
    }
  }, [token, authHeaders, notify, normalizePermissions]);

  // Cargar permisos desde el backend
  const loadPermissions = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(buildApiUrl('/api/permissions'), {
        headers: authHeaders,
      });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      // El endpoint devuelve un array directo, no un objeto con permissions
      const permissionsData = await response.json();
      const permissionsArray = Array.isArray(permissionsData) 
        ? permissionsData 
        : (permissionsData.permissions || []);
      setPermissions(permissionsArray);
    } catch (error) {
      logError('[AdminRoles] Error loading permissions:', error);
      notify({
        title: 'Error al cargar permisos',
        message: 'No se pudieron cargar los permisos disponibles.',
        variant: 'warning',
      });
    }
  }, [token, authHeaders, notify]);

  // Agrupar permisos por categorías
  const groupedPermissions = useMemo(() => {
    const grouped = {};
    
    // Inicializar todas las categorías
    Object.keys(PERMISSION_CATEGORIES).forEach((categoryKey) => {
      grouped[categoryKey] = {
        ...PERMISSION_CATEGORIES[categoryKey],
        items: [],
      };
    });
    
    // Agregar categoría "Otros" para permisos no categorizados
    grouped.OTROS = {
      label: 'Otros',
      icon: 'fas fa-ellipsis-h',
      items: [],
    };
    
    // Agrupar permisos
    permissions.forEach((permission) => {
      const permName = permission.nombre || permission.name;
      let found = false;
      
      // Buscar en qué categoría pertenece
      for (const [categoryKey, category] of Object.entries(PERMISSION_CATEGORIES)) {
        if (category.permissions.includes(permName)) {
          grouped[categoryKey].items.push(permission);
          found = true;
          break;
        }
      }
      
      // Si no se encontró categoría, agregar a "Otros"
      if (!found) {
        grouped.OTROS.items.push(permission);
      }
    });
    
    // Filtrar categorías vacías (excepto si hay items en Otros)
    const filtered = {};
    Object.keys(grouped).forEach((key) => {
      if (grouped[key].items.length > 0 || key === 'OTROS') {
        filtered[key] = grouped[key];
      }
    });
    
    return filtered;
  }, [permissions]);

  useEffect(() => {
    loadRoles();
    loadPermissions();
  }, [loadRoles, loadPermissions]);

  // Handlers de roles
  const handleCreateRole = () => {
    setEditingRole(null);
    setRoleForm({ nombre: '', descripcion: '', permisos: [] });
    setShowRoleModal(true);
  };

  const handleCloseRoleModal = () => {
    setShowRoleModal(false);
    setEditingRole(null);
    setRoleForm({ nombre: '', descripcion: '', permisos: [] });
  };

  const handleEditRole = async (roleId) => {
    try {
      const response = await fetch(buildApiUrl(`/api/roles/${roleId}`), {
        headers: authHeaders,
      });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const role = await response.json();
      setEditingRole(role);
      setRoleForm({
        nombre: role.nombre || '',
        descripcion: role.descripcion || '',
        permisos: normalizePermissions(role.permisos),
      });
      setShowRoleModal(true);
    } catch (error) {
      logError('[AdminRoles] Error loading role:', error);
      notify({
        title: 'Error',
        message: 'No se pudo cargar el rol.',
        variant: 'error',
      });
    }
  };

  const handleViewRole = async (roleId) => {
    try {
      const response = await fetch(buildApiUrl(`/api/roles/${roleId}`), {
        headers: authHeaders,
      });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const role = await response.json();
      setSelectedRole({
        ...role,
        permisos: normalizePermissions(role.permisos),
      });
      setShowViewRoleModal(true);
    } catch (error) {
      logError('[AdminRoles] Error loading role:', error);
      notify({
        title: 'Error',
        message: 'No se pudo cargar el rol.',
        variant: 'error',
      });
    }
  };

  const handleSaveRole = async () => {
    if (!roleForm.nombre.trim()) {
      notify({
        title: 'Nombre requerido',
        message: 'El nombre del rol es obligatorio.',
        variant: 'warning',
      });
      return;
    }
    if (roleForm.permisos.length === 0) {
      notify({
        title: 'Permisos requeridos',
        message: 'Debe seleccionar al menos un permiso.',
        variant: 'warning',
      });
      return;
    }
    try {
      const url = editingRole
        ? buildApiUrl(`/api/roles/${editingRole.id}`)
        : buildApiUrl('/api/roles');
      const method = editingRole ? 'PUT' : 'POST';
      const payload = {
        nombre: roleForm.nombre.trim(),
        descripcion: roleForm.descripcion.trim() || 'Sin descripción',
        permisos: roleForm.permisos,
      };
      const response = await fetch(url, {
        method,
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || `Error ${response.status}`);
      }
      notify({
        title: editingRole ? 'Rol actualizado' : 'Rol creado',
        message: `El rol se ${editingRole ? 'actualizó' : 'creó'} correctamente.`,
        variant: 'success',
      });
      setShowRoleModal(false);
      setEditingRole(null);
      setRoleForm({ nombre: '', descripcion: '', permisos: [] });
      loadRoles();
    } catch (error) {
      logError('[AdminRoles] Error saving role:', error);
      notify({
        title: 'Error al guardar',
        message: error.message || 'No se pudo guardar el rol.',
        variant: 'error',
      });
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este rol? Si hay usuarios asignados, se les asignará automáticamente el rol UsuarioBasico.')) {
      return;
    }
    try {
      const response = await fetch(buildApiUrl(`/api/roles/${roleId}`), {
        method: 'DELETE',
        headers: authHeaders,
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || `Error ${response.status}`);
      }
      
      const data = await response.json().catch(() => ({}));
      
      notify({
        title: 'Rol eliminado',
        message: data.message || 'El rol se eliminó correctamente.',
        variant: 'success',
      });
      
      loadRoles();
    } catch (error) {
      logError('[AdminRoles] Error deleting role:', error);
      notify({
        title: 'Error al eliminar',
        message: error.message || 'No se pudo eliminar el rol.',
        variant: 'error',
      });
    }
  };

  const togglePermission = (permission) => {
    setRoleForm((prev) => ({
      ...prev,
      permisos: prev.permisos.includes(permission)
        ? prev.permisos.filter((p) => p !== permission)
        : [...prev.permisos, permission],
    }));
  };

  if (!user) return null;

  return (
    <div className="admin-dashboard">
      <div className="admin-layout">
        <AdminSidebar
          activeKey="roles"
          onNavigate={(item) => {
            if (item.path) {
              navigate(item.path);
              return;
            }
            notify({
              title: 'Sección en desarrollo',
              message: 'Estamos migrando esta sección al nuevo panel.',
              variant: 'info',
            });
          }}
        />
        <main className="admin-main">
          <div className="ARP-container">
            {/* Header */}
            <div className="ARP-header">
              <div className="ARP-header-content">
                <h1 className="ARP-title">
                  <i className="fas fa-user-shield" />
                  Gestión de Roles
                </h1>
                <p className="ARP-subtitle">
                  Administra roles y permisos del sistema de manera eficiente
                </p>
              </div>
              <div className="ARP-actions">
                <Button variant="primary" onClick={handleCreateRole}>
                  <i className="fas fa-plus me-2" />
                  Nuevo Rol
                </Button>
                <Button variant="outline-secondary" onClick={loadRoles} disabled={loadingRoles}>
                  <i className="fas fa-sync-alt me-2" />
                  Actualizar
                </Button>
              </div>
            </div>

            {/* Grid de Roles */}
            {loadingRoles ? (
              <div className="ARP-loading">
                <Spinner animation="border" />
                <p>Cargando roles...</p>
              </div>
            ) : (
              <div className="ARP-roles-grid">
                {roles.length === 0 ? (
                  <Card className="ARP-empty-roles">
                    <Card.Body>
                      <i className="fas fa-user-shield ARP-empty-icon" />
                      <h5>No hay roles disponibles</h5>
                      <p>Crea el primer rol para comenzar</p>
                      <Button variant="primary" onClick={handleCreateRole}>
                        Crear Rol
                      </Button>
                    </Card.Body>
                  </Card>
                ) : (
                  roles.map((role) => (
                    <Card key={role.id} className="ARP-role-card">
                      <Card.Body>
                        <div className="ARP-role-header">
                          <h3 className="ARP-role-name">{role.nombre}</h3>
                          <Badge bg="primary">{role.nombre}</Badge>
                        </div>
                        <div className="ARP-role-description">
                          {role.descripcion || 'Sin descripción'}
                        </div>
                        <div className="ARP-role-permissions">
                          <div className="ARP-role-permissions-title">Permisos:</div>
                          <div className="ARP-permissions-list">
                            {(() => {
                              const normalizedPerms = normalizePermissions(role.permisos);
                              return normalizedPerms.slice(0, 5).map((perm, index) => (
                                <Badge key={`${role.id}-perm-${index}-${perm}`} bg="info" className="ARP-permission-tag">
                                  {perm}
                              </Badge>
                              ));
                            })()}
                            {(() => {
                              const normalizedPerms = normalizePermissions(role.permisos);
                              return normalizedPerms.length > 5 && (
                                <Badge key={`${role.id}-more`} bg="secondary">
                                  +{normalizedPerms.length - 5} más
                              </Badge>
                              );
                            })()}
                          </div>
                        </div>
                        <div className="ARP-role-stats">
                          <div className="ARP-role-stat">
                            <div className="ARP-role-stat-number">
                              {role.usuariosCount || 0}
                            </div>
                            <div className="ARP-role-stat-label">Usuarios</div>
                          </div>
                          <div className="ARP-role-stat">
                            <div className="ARP-role-stat-number">
                              {normalizePermissions(role.permisos).length}
                            </div>
                            <div className="ARP-role-stat-label">Permisos</div>
                          </div>
                        </div>
                        <div className="ARP-role-actions">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleViewRole(role.id)}
                          >
                            <i className="fas fa-eye me-1" />
                            Ver
                          </Button>
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => handleEditRole(role.id)}
                          >
                            <i className="fas fa-edit me-1" />
                            Editar
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteRole(role.id)}
                          >
                            <i className="fas fa-trash me-1" />
                            Eliminar
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  ))
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modal Crear/Editar Rol */}
      <Modal show={showRoleModal} onHide={handleCloseRoleModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingRole ? 'Editar Rol' : 'Crear Nuevo Rol'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nombre del Rol *</Form.Label>
              <Form.Control
                type="text"
                value={roleForm.nombre}
                onChange={(e) => setRoleForm({ ...roleForm, nombre: e.target.value })}
                placeholder="Ej: Editor, Moderador, etc."
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={roleForm.descripcion}
                onChange={(e) => setRoleForm({ ...roleForm, descripcion: e.target.value })}
                placeholder="Describe las responsabilidades de este rol..."
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Permisos *</Form.Label>
              <div className="ARP-permissions-container">
                {Object.entries(groupedPermissions).map(([categoryKey, category]) => {
                  if (category.items.length === 0) return null;
                  
                  return (
                    <div key={categoryKey} className="ARP-permission-category">
                      <div className="ARP-permission-category-header">
                        <i className={category.icon} />
                        <h6 className="ARP-permission-category-title">{category.label}</h6>
                        <Badge bg="secondary" className="ARP-permission-category-count">
                          {category.items.length}
                        </Badge>
                      </div>
              <div className="ARP-permissions-grid">
                        {category.items.map((permission) => {
                          const permName = permission.nombre || permission.name;
                          return (
                            <div key={permission.id || permName} className="ARP-permission-checkbox">
                    <Form.Check
                      type="checkbox"
                                id={`perm-${permission.id || permName}`}
                                checked={roleForm.permisos.includes(permName)}
                                onChange={() => togglePermission(permName)}
                                label={
                                  <span>
                                    {permName}
                                    {permission.descripcion && (
                                      <small className="ARP-permission-description">
                                        {' '}- {permission.descripcion}
                                      </small>
                                    )}
                                  </span>
                                }
                    />
                  </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseRoleModal}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveRole}>
            <i className="fas fa-save me-2" />
            Guardar Rol
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Ver Rol */}
      <Modal show={showViewRoleModal} onHide={() => setShowViewRoleModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Detalles del Rol: {selectedRole?.nombre}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRole && (
            <div className="ARP-role-details">
              <div className="ARP-role-detail-item">
                <div className="ARP-role-detail-label">Nombre</div>
                <div className="ARP-role-detail-value">{selectedRole.nombre}</div>
              </div>
              <div className="ARP-role-detail-item">
                <div className="ARP-role-detail-label">Descripción</div>
                <div className="ARP-role-detail-value">
                  {selectedRole.descripcion || 'Sin descripción'}
                </div>
              </div>
              <div className="ARP-role-detail-item">
                <div className="ARP-role-detail-label">
                  Permisos ({normalizePermissions(selectedRole.permisos).length})
                </div>
                <div className="ARP-role-permissions-display">
                  {(() => {
                    const normalizedPerms = normalizePermissions(selectedRole.permisos);
                    return normalizedPerms.map((perm, index) => {
                      // Buscar el permiso en la lista de permisos cargados para obtener descripción
                      const permissionObj = permissions.find(
                        (p) => (p.nombre || p.name) === perm
                      );
                      return (
                        <Badge key={`view-perm-${index}-${perm}`} bg="info" className="ARP-permission-tag" title={permissionObj?.descripcion || ''}>
                          {perm}
                    </Badge>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowViewRoleModal(false)}>
            Cerrar
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowViewRoleModal(false);
              if (selectedRole) handleEditRole(selectedRole.id);
            }}
          >
            <i className="fas fa-edit me-2" />
            Editar Rol
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const AdminRolesPage = () => (
  <AdminNotificationsProvider>
    <AdminRolesPageInner />
  </AdminNotificationsProvider>
);

export default AdminRolesPage;

